import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import {
  type BuildAlignOptions,
  buildAlignCenterItems,
  buildAlignEdgeItems,
} from '../helpers/align-items'
import { readLiveSelection } from '../helpers/live-selection'
import type { AlignToolKey } from '../types/layout-operations'
import type { OnBatchShapesCommitted } from '../types/layout-panel-props'
import { commitShapeBatch } from './commit-shape-batch'

/**
 * Aligns the live selection (2+ visible shapes). 'top'/'left' align to the min edge,
 * 'bottom'/'right' to the max edge; 'center' aligns vertical centers (Y axis) and 'middle'
 * aligns horizontal centers (X axis). A selected text frame is a shape now (text-unification)
 * and is already in the selection, so there is no separate text-only branch to handle here.
 */
export function handleAlign(
  mgr: ShapeManagerRef | null | undefined,
  onBatchShapesCommitted: OnBatchShapesCommitted | undefined,
  key: AlignToolKey
): void {
  const shapes = readLiveSelection(mgr).selectedShapes

  if (shapes.length < 2 || !mgr || !onBatchShapesCommitted) {
    return
  }

  const getBounds = (id: string) => mgr.getShapeBounds?.(id)
  const refId = mgr.getMultiSelectAlignReferenceShapeId?.() ?? undefined
  const options = { referenceShapeId: refId, minSelectionForReference: 2 }

  const items = buildItemsForKey(key, shapes, getBounds, options)

  commitShapeBatch(mgr, items, onBatchShapesCommitted, { kind: 'position' })
}

function buildItemsForKey(
  key: AlignToolKey,
  shapes: ShapeData[],
  getBounds: (id: string) => ShapeBounds | undefined,
  options: BuildAlignOptions
): BatchShapeTransformItem[] {
  switch (key) {
    case 'top':
      return buildAlignEdgeItems(shapes, getBounds, 'y', 'min', options)
    case 'left':
      return buildAlignEdgeItems(shapes, getBounds, 'x', 'min', options)
    case 'bottom':
      return buildAlignEdgeItems(shapes, getBounds, 'y', 'max', options)
    case 'right':
      return buildAlignEdgeItems(shapes, getBounds, 'x', 'max', options)
    case 'center':
      return buildAlignCenterItems(shapes, getBounds, 'y', options)
    case 'middle':
      return buildAlignCenterItems(shapes, getBounds, 'x', options)
    default: {
      const _exhaustive: never = key

      throw new Error(`Unexpected align tool key: ${String(_exhaustive)}`)
    }
  }
}
